import { createRef, PureComponent } from 'react'
import PropTypes from 'prop-types'
import withStyles from 'beam/dist/styles'
import { getPointerPosition } from 'beam/dist/utils'
import classNames from 'classnames'
import { msToHHMMSS } from 'beam/dist/utils/time'
import TimeMarker from './TimeMarker'
import Playback from './Playback'

export const autoQaIdPrefix = 'App-Recordings-MediaPlayer-SeekBar-'

const styles = theme => ({
  root: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'sans-serif',
    padding: theme.spacing(0, 0, 0, 0.5),
    height: '100%',
    width: theme.spacing(3),
    zIndex: 99,
  },
  barContainer: {
    position: 'relative',
    boxSizing: 'content-box !important',
    padding: theme.spacing(0, 1),
    width: theme.spacing(0.5),
    height: '100%',
  },
  bar: {
    background: 'rgba(0,0,0,0.4)',
    position: 'absolute',
    top: theme.spacing(1),
    cursor: 'pointer',
    left: theme.spacing(1),
    height: '100%',
    width: theme.spacing(0.5),
  },
  seekBar: {
    background: 'rgba(0,0,0,0.3)',
    position: 'absolute',
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity .3s linear',
    cursor: 'pointer',
    top: 0,
    left: theme.spacing(1),
    width: theme.spacing(0.5),
  },
  seekTime: {
    borderRadius: '3px',
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.text.primary,
    color: theme.palette.common.white,
    fontSize: '12px',
    zIndex: 9999,
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity .3s linear',
    position: 'absolute',
    left: '100%',
    transform: 'translateY(-50%)',
  },
  seekBarVisible: {
    opacity: 1,
    visibility: 'visible',
  },
  seeking: {
    top: 0,
    left: 7,
    width: 6,
    height: '100%',
  },
})

class SeekBar extends PureComponent {
  static propTypes = {
    /** For styling via JSS */
    classes: PropTypes.object.isRequired,
    /** Dark or light variant */
    variant: PropTypes.oneOf(['dark', 'light']),
    /** Total length of media in milliseconds */
    duration: PropTypes.number,
    /** Current playback time in milliseconds */
    currentTime: PropTypes.number,
    /** Width of the red time marker line */
    timeMarkerWidth: PropTypes.number,
    /** Is the media player in zoomed mode or not */
    isZoomed: PropTypes.bool,
    /** Callback for when scrubbing starts */
    onScrubStart: PropTypes.func.isRequired,
    /** Callback for when scrubbing ends */
    onScrubEnd: PropTypes.func.isRequired,
  }

  static defaultProps = {
    variant: 'dark',
    duration: 0,
    currentTime: 0,
    buffer: 0,
    timeMarkerWidth: 0,
  }

  state = {
    completed: 0,
    seekingTime: 0,
    seekingPosition: 0,
    seeking: false,
    scrubbing: false,
  }

  constructor(props) {
    super(props)
    this.barContainer = createRef()
    this.labelRef = createRef()
  }

  componentDidMount() {
    document.addEventListener('mousemove', this.handleScrubbing)
    document.addEventListener('mouseup', this.handleScrubbingDone)
    this.setState({
      completed: this.props.currentTime,
    })
  }

  componentDidUpdate(prevProps) {
    if (!this.state.scrubbing && this.props.currentTime !== prevProps.currentTime) {
      this.setCompleted()
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleScrubbing)
    document.removeEventListener('mouseup', this.handleScrubbingDone)
  }

  setCompleted = () => this.setState({ completed: this.props.currentTime })

  calculateDistance = event => {
    const position = getPointerPosition(this.barContainer.current, event)
    return 1 - position.y
  }

  getNewTime = event => {
    const { duration } = this.props
    const totalSeconds = (duration + 1000) / 1000
    const distance = this.calculateDistance(event)
    const newTime = distance * (totalSeconds - 1)

    // Don't let video end while scrubbing.
    return parseInt((newTime === totalSeconds ? newTime - 0.1 : newTime) * 1000, 10)
  }

  /** Handle case where user has moved cursor off of the seekbar */
  handleScrubbing = event => {
    const { scrubbing } = this.state

    if (scrubbing) {
      const newTime = this.getNewTime(event)
      this.setState({
        completed: newTime,
      })
    }
    return false
  }

  /** Handle case where user has moused up after moving cursor off of seekbar */
  handleScrubbingDone = event => {
    const { scrubbing } = this.state

    if (scrubbing) {
      this.handleMouseUp(event)
    }
    return false
  }

  handleMouseDown = event => {
    const newTime = this.getNewTime(event)

    event.preventDefault()
    this.props.onScrubStart(event)
    this.setState({
      completed: newTime,
      scrubbing: true,
    })
  }

  handleMouseUp = event => {
    const newTime = this.getNewTime(event)

    this.props.onScrubEnd(newTime)
    this.setState({
      completed: newTime,
      scrubbing: false,
    })
  }

  handleMouseMove = event => {
    const newTime = this.getNewTime(event)
    const seekPosition = Math.round(
      this.barContainer.current.getBoundingClientRect().height *
        this.calculateDistance(event),
    )

    this.setState({
      seekingTime: newTime,
      seekingPosition: seekPosition,
    })
  }

  render() {
    const { classes, variant, duration, timeMarkerWidth, isZoomed } = this.props
    const { completed, seekingTime, seekingPosition, seeking, scrubbing } = this.state

    const completedPercent = (completed / duration) * 100
    const seekPercent = (seekingTime / duration) * 100
    const minSeekTimePosition = 16
    const maxSeekTimePosition = this.barContainer.current
      ? Math.round(
        this.barContainer.current.getBoundingClientRect().height,
      ) - 20
      : 0

    return (
      <div
        className={classNames(classes.root, {
          [classes.rootDark]: variant === 'dark',
          [classes.rootLight]: variant === 'light',
        })}
      >
        <div
          ref={this.barContainer}
          className={classes.barContainer}
          role="button"
          tabIndex={0}
          onMouseOver={() => this.setState({ seeking: true })}
          onFocus={() => this.setState({ seeking: true })}
          onMouseDown={this.handleMouseDown}
          onMouseMove={this.handleMouseMove}
          onMouseUp={this.handleMouseUp}
          onMouseOut={() => this.setState({ seeking: false })}
          onBlur={() => this.setState({ seeking: false })}
          onDragStart={() => false}
        >
          <div className={classNames(classes.bar, seeking && classes.seeking)} onDragStart={() => false} />
          <div
            className={classNames(classes.seekBar, seeking && classes.seekBarVisible, seeking && classes.seeking)}
            style={{ height: `${seekPercent}%` }}
            data-time={msToHHMMSS(seekingTime)}
            onDragStart={() => false}
          />
          <Playback
            classes={{ seeking: classes.seeking }}
            isSeeking={seeking}
            isScrubbing={scrubbing}
            completedPercent={completedPercent}
          />
          {
            timeMarkerWidth === 0 ? null : (
              <TimeMarker
                isZoomed={isZoomed}
                isScrubbing={scrubbing}
                completed={completed}
                duration={duration}
                width={timeMarkerWidth}
                containerRef={this.barContainer}
              />
            )
          }
          <div
            className={classNames(classes.seekTime, seeking && classes.seekBarVisible)}
            style={{
              top: `${Math.max(
                minSeekTimePosition,
                Math.min(maxSeekTimePosition, seekingPosition),
              )}px`,
            }}
            data-testid={`${autoQaIdPrefix}seekTime`}
          >
            {msToHHMMSS(seekingTime)}
          </div>
        </div>
      </div>
    )
  }
}

export { SeekBar as UnstyledSeekBar }
export default withStyles(styles)(SeekBar)
